/**
 * Lightweight session telemetry for debugging/support.
 *
 * Performance budget (deliberate):
 * - In-memory ring buffer (600 events max) — push is O(1), no I/O.
 * - Event sources are all low-frequency (VPS ~1/4 s, state changes, route
 *   calcs). NOTHING is logged per-frame.
 * - localStorage is written ONLY at session end (last 2 sessions kept).
 * - Export builds the JSON on demand.
 */

import { APP_VERSION, BUILD_TIME } from '../version';
import { FpsStats } from '../utils/math';
import { diagDisabledList } from '../utils/diagnostics';
import { loadConfig } from './configService';

export type DebugCategory =
  | 'session'
  | 'state'
  | 'vps'
  | 'pose'
  | 'route'
  | 'navmesh'
  | 'admin'
  | 'error';

interface DebugEvent {
  /** Seconds since app start, 1 decimal. */
  t: number;
  cat: DebugCategory;
  msg: string;
  data?: unknown;
}

const MAX_EVENTS = 600;
const STORAGE_KEY = 'navme.debug.sessions.v1';
const APP_START = Date.now();

const events: DebugEvent[] = [];

/** Aggregates — updated incrementally, rendered by the debug panel. */
const stats = {
  vpsOk: 0,
  vpsFail: 0,
  confSum: 0,
  confCount: 0,
  confMin: null as number | null,
  lastConf: null as number | null,
  /** Per-attempt duration (screenshot → VPS response), ms. */
  vpsMsSum: 0,
  vpsMsCount: 0,
  vpsLastMs: null as number | null,
  /** Seconds from AR session start to first successful localization. */
  firstFixS: null as number | null,
  correctionsDeadbanded: 0,
  correctionsEased: 0,
  correctionsSnapped: 0,
  lastCorrectionM: null as number | null,
  routeKind: null as string | null,
  navmeshSource: null as string | null,
};

/** Rendered-FPS aggregate (min/avg/last) — fed the smoothed per-frame FPS. */
const fps = new FpsStats();

/**
 * Record one smoothed FPS sample (call once per rendered frame from the XR
 * loop). Cheap: a few adds. Surfaces min/avg/last in the 🐞 export so FPS is
 * scoreable from the log instead of read off the video.
 */
export function recordFps(smoothedFps: number): void {
  fps.add(smoothedFps);
}

export function logEvent(cat: DebugCategory, msg: string, data?: unknown): void {
  events.push({ t: Math.round((Date.now() - APP_START) / 100) / 10, cat, msg, data });
  if (events.length > MAX_EVENTS) events.shift();
}

/**
 * Read-only view of the in-memory event ring. Used by unit/replay tests to
 * assert on logged decisions (e.g. "⚠️ VPS OSCILLATION" fired exactly once)
 * — and by any future structured-trace export.
 */
export function getEvents(): ReadonlyArray<DebugEvent> {
  return events;
}

// ---- Time to first localization (mode-agnostic: WebXR + camera) ----

let arSessionStartAt: number | null = null;

/** Call from onSessionStart (both AR modes). Resets the first-fix clock. */
export function markArSessionStart(): void {
  arSessionStartAt = performance.now();
  stats.firstFixS = null;
  fps.reset();
  logEvent('session', 'AR session started — first-fix clock running');
}

/** Call on every successful localization; records only the first per session. */
export function markLocalized(): void {
  if (arSessionStartAt === null || stats.firstFixS !== null) return;
  stats.firstFixS = Math.round((performance.now() - arSessionStartAt) / 100) / 10;
  logEvent('vps', `time to first fix: ${stats.firstFixS}s`);
}

// ---- Typed helpers that also maintain aggregates ----

export function logVpsResult(ok: boolean, confidence?: number, detail?: string, ms?: number): void {
  if (ok) {
    stats.vpsOk++;
    if (confidence !== undefined) {
      stats.confSum += confidence;
      stats.confCount++;
      stats.lastConf = confidence;
      stats.confMin = stats.confMin === null ? confidence : Math.min(stats.confMin, confidence);
    }
  } else {
    stats.vpsFail++;
  }
  if (ms !== undefined) {
    stats.vpsMsSum += ms;
    stats.vpsMsCount++;
    stats.vpsLastMs = Math.round(ms);
  }
  const msTxt = ms !== undefined ? ` ${Math.round(ms)}ms` : '';
  logEvent('vps', (ok ? `ok conf=${confidence?.toFixed(2) ?? '?'}` : `fail ${detail ?? ''}`) + msTxt);
}

export function logCorrection(
  kind: 'deadband' | 'eased' | 'snap',
  deltaM: number,
  deltaDeg: number,
  confidence?: number,
): void {
  if (kind === 'deadband') stats.correctionsDeadbanded++;
  else if (kind === 'eased') stats.correctionsEased++;
  else stats.correctionsSnapped++;
  stats.lastCorrectionM = deltaM;
  const conf = confidence !== undefined ? ` (conf ${confidence.toFixed(2)})` : '';
  logEvent('pose', `${kind} Δ${deltaM.toFixed(2)}m ${deltaDeg.toFixed(1)}°${conf}`);
}

export function logRoute(kind: string, nodeCount: number, distanceM: number, dest: string): void {
  stats.routeKind = kind;
  logEvent('route', `${kind} ${nodeCount} nodes ${distanceM.toFixed(1)}m → ${dest}`);
}

export function logNavmesh(source: string, ms?: number): void {
  stats.navmeshSource = source;
  logEvent('navmesh', ms !== undefined ? `${source} (${Math.round(ms)}ms)` : source);
}

// ---- Pose trail (session replay) ----

export interface PoseSample {
  /** Seconds since app start. */
  t: number;
  x: number;
  y: number;
  z: number;
}

const MAX_POSE_SAMPLES = 900; // 15 min at 1 Hz
const poseTrail: PoseSample[] = [];

/** Call ~1/s during AR — records the walked path for Dashboard replay. */
export function recordPoseSample(x: number, y: number, z: number): void {
  poseTrail.push({
    t: Math.round((Date.now() - APP_START) / 100) / 10,
    x: +x.toFixed(2),
    y: +y.toFixed(2),
    z: +z.toFixed(2),
  });
  if (poseTrail.length > MAX_POSE_SAMPLES) poseTrail.shift();
}

/** Walked path of the most recent PERSISTED session (for Dashboard replay). */
export function getLastSessionTrail(): PoseSample[] {
  try {
    const sessions = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') as Array<{
      poseTrail?: PoseSample[];
    }>;
    for (let i = sessions.length - 1; i >= 0; i--) {
      const trail = sessions[i].poseTrail;
      if (trail && trail.length > 1) return trail;
    }
  } catch {
    /* ignore */
  }
  // Fall back to the in-progress session's trail (e.g. same-device testing).
  return [...poseTrail];
}

// ---- Localization heatmap samples (persisted; Dashboard renders them) ----

export interface LocSample {
  x: number;
  y: number;
  z: number;
  /** VPS confidence 0..1 at this position. */
  c: number;
  /** MultiSet map code this fix belongs to. All venues on a device share ONE
   *  localStorage bucket (2026-07-17: found via a "loc coach always says the
   *  same thing" report — a multi-venue test device's samples were mixing
   *  unrelated venues' map-space coordinates into one heatmap/zone cluster).
   *  Optional so pre-tagging entries still deserialize; they're excluded
   *  from getLocSamplesForMap() rather than trusted to belong anywhere. */
  map?: string;
}

const LOC_SAMPLES_KEY = 'navme.locSamples.v1';
const MAX_LOC_SAMPLES = 600;

/** Record a successful localization's map-space position + confidence. */
export function recordLocSample(s: LocSample): void {
  try {
    const arr = JSON.parse(localStorage.getItem(LOC_SAMPLES_KEY) ?? '[]') as LocSample[];
    const map = loadConfig().code || undefined;
    arr.push({ x: +s.x.toFixed(2), y: +s.y.toFixed(2), z: +s.z.toFixed(2), c: +s.c.toFixed(2), ...(map ? { map } : {}) });
    while (arr.length > MAX_LOC_SAMPLES) arr.shift();
    localStorage.setItem(LOC_SAMPLES_KEY, JSON.stringify(arr));
  } catch {
    /* quota — drop */
  }
}

export function getLocSamples(): LocSample[] {
  try {
    return JSON.parse(localStorage.getItem(LOC_SAMPLES_KEY) ?? '[]') as LocSample[];
  } catch {
    return [];
  }
}

/** Samples belonging to one venue — use this everywhere heatmap/coaching
 *  data is consumed. Untagged legacy samples are excluded (see LocSample.map). */
export function getLocSamplesForMap(mapCode: string): LocSample[] {
  return getLocSamples().filter((s) => s.map === mapCode);
}

export function clearLocSamples(): void {
  localStorage.removeItem(LOC_SAMPLES_KEY);
}

/** Compact live-stats lines for the debug panel. */
export function getSummaryLines(): string[] {
  const avg = stats.confCount ? (stats.confSum / stats.confCount).toFixed(2) : '—';
  const msAvg = stats.vpsMsCount ? `${Math.round(stats.vpsMsSum / stats.vpsMsCount)}` : '—';
  return [
    `vps:   ${stats.vpsOk}✓ ${stats.vpsFail}✗  conf avg ${avg} min ${stats.confMin?.toFixed(2) ?? '—'} last ${stats.lastConf?.toFixed(2) ?? '—'}`,
    `vps t: last ${stats.vpsLastMs ?? '—'}ms · avg ${msAvg}ms · first fix ${stats.firstFixS !== null ? `${stats.firstFixS}s` : '—'}`,
    `corr:  ${stats.correctionsDeadbanded} noise / ${stats.correctionsEased} eased / ${stats.correctionsSnapped} snap  lastΔ ${stats.lastCorrectionM?.toFixed(2) ?? '—'}m`,
    `fps:   last ${fps.last ?? '—'} · avg ${fps.avg !== null ? Math.round(fps.avg) : '—'} · min ${fps.min !== null ? Math.round(fps.min) : '—'}`,
    `src:   route=${stats.routeKind ?? '—'} navmesh=${stats.navmeshSource ?? '—'}`,
  ];
}

// ---- Report building / persistence / export ----

function buildReport(): Record<string, unknown> {
  return {
    generatedAt: new Date().toISOString(),
    app: 'NavMe',
    version: APP_VERSION,
    buildTime: BUILD_TIME,
    device: {
      userAgent: navigator.userAgent,
      screen: `${screen.width}x${screen.height}@${window.devicePixelRatio}`,
      online: navigator.onLine,
    },
    // Diagnostic subsystem toggles active this session (?no=…), empty = all on.
    diagDisabled: diagDisabledList(),
    stats: {
      ...stats,
      fpsLast: fps.last,
      fpsAvg: fps.avg !== null ? Math.round(fps.avg * 10) / 10 : null,
      fpsMin: fps.min !== null ? Math.round(fps.min * 10) / 10 : null,
    },
    poseTrail: [...poseTrail],
    events: [...events],
  };
}

/** Call at session end — keeps the last 2 session reports in localStorage. */
export function persistSession(reason: string): void {
  try {
    logEvent('session', `persist (${reason})`);
    const prev = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') as unknown[];
    prev.push(buildReport());
    while (prev.length > 2) prev.shift();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prev));
  } catch {
    /* quota — drop silently, never break the app for telemetry */
  }
}

/** One-tap export: share sheet on mobile when possible, else file download. Includes persisted previous sessions for context. */
export async function exportReport(): Promise<void> {
  let previous: unknown[] = [];
  try {
    previous = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') as unknown[];
  } catch {
    /* ignore */
  }
  const full = { current: buildReport(), previousSessions: previous };
  const json = JSON.stringify(full, null, 1);
  const file = new File([json], `navme-debug-${Date.now()}.json`, { type: 'application/json' });

  // iOS Safari: share sheet is the reliable path for getting a file off-device.
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: 'NavMe debug report' });
      return;
    } catch {
      /* user cancelled or share failed — fall through to download */
    }
  }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
  a.download = file.name;
  a.click();
  URL.revokeObjectURL(a.href);
}
